import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PaymentFilters } from "../api/users";
import { UserAutocomplete } from "./UserAutocomplete";

interface PaymentsFiltersProps {
    filters: PaymentFilters;
    onFiltersChange: (filters: PaymentFilters) => void;
    uniqueDescs: string[];
}

export const PaymentsFilters: React.FC<PaymentsFiltersProps> = ({
    filters,
    onFiltersChange,
    uniqueDescs,
}) => {
    const { t } = useTranslation("payments");

    // Состояние для отслеживания введенного текста в UserAutocomplete
    const [userInputValue, setUserInputValue] = useState(filters.user_id || "");

    // Синхронизируем локальное состояние с фильтрами при их изменении извне
    useEffect(() => {
        setUserInputValue(filters.user_id || "");
    }, [filters.user_id]);

    // Хардкодные опции для anual
    const anualOptions = [
        { value: 1, label: t("anual.structure") },
        { value: 2, label: t("anual.welfare") },
        { value: 3, label: t("anual.tourism") },
        { value: 4, label: t("anual.star") },
    ];

    const handleFilterChange = (
        key: keyof PaymentFilters,
        value: string | number
    ) => {
        onFiltersChange({ ...filters, [key]: value });
    };

    const handleClearFilters = () => {
        onFiltersChange({
            per_page: 20,
            page: 1,
        });
    };

    return (
        <div className="space-y-4 bg-white dark:bg-gray-800 rounded-md p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("user_id")}
                    </label>
                    <UserAutocomplete
                        value={userInputValue}
                        onChange={(value) => {
                            // Обновляем только локальное состояние при вводе текста
                            setUserInputValue(value);
                        }}
                        onUserSelect={(user) => {
                            // Когда пользователь выбирает из списка, обновляем и локальное состояние, и фильтр
                            setUserInputValue(user.id.toString());
                            handleFilterChange("user_id", user.id.toString());
                        }}
                        onBlur={() => {
                            // При потере фокуса, если введены только цифры, применяем фильтр
                            if (/^\d+$/.test(userInputValue)) {
                                handleFilterChange("user_id", userInputValue);
                            } else if (userInputValue.trim() === "") {
                                // Если поле пустое, очищаем фильтр
                                handleFilterChange("user_id", "");
                            }
                        }}
                        onKeyDown={(e) => {
                            // При нажатии Enter, если введены только цифры, применяем фильтр
                            if (
                                e.key === "Enter" &&
                                /^\d+$/.test(userInputValue)
                            ) {
                                handleFilterChange("user_id", userInputValue);
                            }
                        }}
                        placeholder={t("user_id_placeholder")}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("user_email")}
                    </label>
                    <input
                        type="email"
                        value={filters.user_email || ""}
                        onChange={(e) =>
                            handleFilterChange("user_email", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                        placeholder={t("user_email_placeholder")}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("user_name")}
                    </label>
                    <input
                        type="text"
                        value={filters.user_name || ""}
                        onChange={(e) =>
                            handleFilterChange("user_name", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                        placeholder={t("user_name_placeholder")}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Валюта
                    </label>
                    <select
                        value={filters.currency || ""}
                        onChange={(e) =>
                            handleFilterChange("currency", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                    >
                        <option
                            value=""
                            className="text-gray-900 dark:text-white"
                        >
                            {t("all_currencies")}
                        </option>
                        <option
                            value="0"
                            className="text-gray-900 dark:text-white"
                        >
                            USD
                        </option>
                        <option
                            value="1"
                            className="text-gray-900 dark:text-white"
                        >
                            USDT
                        </option>
                        <option
                            value="100"
                            className="text-gray-900 dark:text-white"
                        >
                            {t("currency.none")}
                        </option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("min_amount")}
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        value={filters.min_amount || ""}
                        onChange={(e) =>
                            handleFilterChange("min_amount", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                        placeholder={t("amount_placeholder")}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("max_amount")}
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        value={filters.max_amount || ""}
                        onChange={(e) =>
                            handleFilterChange("max_amount", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                        placeholder={t("amount_placeholder")}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("date_from")}
                    </label>
                    <input
                        type="datetime-local"
                        value={filters.date_from || ""}
                        onChange={(e) =>
                            handleFilterChange("date_from", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("date_to")}
                    </label>
                    <input
                        type="datetime-local"
                        value={filters.date_to || ""}
                        onChange={(e) =>
                            handleFilterChange("date_to", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.desc")}
                    </label>
                    <select
                        value={filters.desc || ""}
                        onChange={(e) =>
                            handleFilterChange("desc", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                    >
                        <option
                            value=""
                            className="text-gray-900 dark:text-white"
                        >
                            {t("filters.all_descs")}
                        </option>
                        {uniqueDescs.map((desc) => (
                            <option
                                key={desc}
                                value={desc}
                                className="text-gray-900 dark:text-white"
                            >
                                {desc}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("filters.anual")}
                    </label>
                    <select
                        value={filters.anual || ""}
                        onChange={(e) =>
                            handleFilterChange("anual", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                    >
                        <option
                            value=""
                            className="text-gray-900 dark:text-white"
                        >
                            {t("filters.all_anual")}
                        </option>
                        {anualOptions.map((option) => (
                            <option
                                key={option.value}
                                value={option.value.toString()}
                                className="text-gray-900 dark:text-white"
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex gap-2">
                <button
                    onClick={() => onFiltersChange({ ...filters, page: 1 })}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                    {t("search")}
                </button>
                <button
                    onClick={handleClearFilters}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                    {t("clear")}
                </button>
            </div>
        </div>
    );
};
